'use client'

import { cn } from '@brew/ui/cn'
import { useState } from 'react'
import { GOLDEN_WORD_FILL } from './golden-word'
import styles from './rotating-word.module.css'

/**
 * The hero headline's last word. Every word sits invisibly in the same grid
 * cell, so the slot is always as wide as the longest word: the headline
 * never reflows or re-centers as the words change. Where the headline
 * wraps (below `xl`) the word is centred in that slot, so on a phone, where
 * it has a line of its own, a short word sits centred rather than off to
 * the left; on the one-line headline it starts at the slot's left edge,
 * right after "for". The sizers draw their word as CSS content, so the
 * heading's text stays one sentence. It server-renders
 * `words[0]`, then a CSS loop (`rotating-word.module.css`) fades each word
 * in and out and every loop boundary advances to the next one. Reduced
 * motion stops the loop, which leaves the first word in place.
 */
export function RotatingWord({ words }: { words: ReadonlyArray<string> }) {
  const [index, setIndex] = useState(0)
  const isCycling = words.length > 1

  return (
    <span className="inline-grid whitespace-nowrap text-center xl:text-left">
      {words.map((word) => (
        <span
          key={word}
          aria-hidden="true"
          data-word={word}
          className="invisible [grid-area:1/1] after:content-[attr(data-word)]"
        />
      ))}
      <span
        className={cn('[grid-area:1/1]', GOLDEN_WORD_FILL, {
          [styles.word]: isCycling,
        })}
        onAnimationIteration={() =>
          setIndex((current) => (current + 1) % words.length)
        }
      >
        {words[index]}
      </span>
    </span>
  )
}
